// Global
import React from 'react';

// Local
import { ProductResponse } from 'lib/services/models/content-response';
import useDictionary from 'lib/hooks/useDictionary';

import styles from './ProductDetails.module.scss';

export interface ProductDetailsProps {
  product?: ProductResponse;
}

/**
 * Rounds a decimal string to 1 decimal place and strips trailing ".0".
 * e.g. "1.066" → "1.1", "10.000" → "10", "15.680" → "15.7"
 */
function formatDimension(value: string | null | undefined): string | null {
  if (!value) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return parseFloat(num.toFixed(1)).toString();
}

function combineValueUnit(value: string | null, unit: string | null | undefined): string | null {
  if (!value) return null;
  return unit ? `${value} ${unit}` : value;
}

export function ProductDetails({ product }: ProductDetailsProps) {
  const { getDictionaryValue } = useDictionary();

  if (!product) {
    return null;
  }

  if (!product) {
    return null;
  }

  const notAvailable =
    getDictionaryValue('Components.Product.ProductDetails.NotAvailable') || 'N/A';

  // Product Code: strip leading zeros
  const productCodeRaw = product.ProductId ?? null;
  const productCode = productCodeRaw ? parseInt(productCodeRaw, 10).toString() : null;

  // Net Weight: always renders — shows N/A when null
  const netWeight = product.NetWeight ?? null;

  // UPC: use packageEanUpc
  const upc = product.PackageEanUpc ?? null;

  // Volume
  const volume = combineValueUnit(formatDimension(product.Volume), product.VolumeUnit);

  // GTIN: gtin14 only
  const gtin = product.Gtin14 ?? null;

  // Height
  const height = combineValueUnit(formatDimension(product.Height), product.HeightUnit);

  // Unit Size: always renders — shows N/A when null  (unit is built into the value; no separated field needed)
  const unitSize = product.UnitSize ?? null;

  // Length (depth in API)
  const length = combineValueUnit(formatDimension(product.Depth), product.DepthUnit);

  // Case Count
  const caseCount = product.CaseCount ?? null;

  // Width
  const width = combineValueUnit(formatDimension(product.Width), product.WidthUnit);

  // Case Size
  const caseSize = combineValueUnit(formatDimension(product.CaseSize), product.CaseSizeUnit);

  const headline =
    getDictionaryValue('Components.Product.ProductDetails.ProductDetailsHeadline') ||
    'Product Details';
  const productCodeLabel =
    getDictionaryValue('Components.Product.ProductDetails.ProductCode') || 'Product Code';
  const netWeightLabel =
    getDictionaryValue('Components.Product.ProductDetails.NetWeight') || 'Net Weight';
  const upcLabel = getDictionaryValue('Components.Product.ProductDetails.UPC') || 'UPC';
  const volumeLabel = getDictionaryValue('Components.Product.ProductDetails.Volume') || 'Volume';
  const gtinLabel = getDictionaryValue('Components.Product.ProductDetails.GTIN') || 'GTIN';
  const heightLabel = getDictionaryValue('Components.Product.ProductDetails.Height') || 'Height';
  const unitSizeLabel =
    getDictionaryValue('Components.Product.ProductDetails.UnitSize') || 'Unit Size';
  const lengthLabel = getDictionaryValue('Components.Product.ProductDetails.Length') || 'Length';
  const caseCountLabel =
    getDictionaryValue('Components.Product.ProductDetails.CaseCount') || 'Case Count';
  const widthLabel = getDictionaryValue('Components.Product.ProductDetails.Width') || 'Width';
  const caseSizeLabel =
    getDictionaryValue('Components.Product.ProductDetails.CaseSize') || 'Case Size';

  // All fields always render. Net Weight shows N/A when null; other fields hide when null.
  // Order matches the production site display.
  const fields: { label: string; value: string | null; alwaysShow?: boolean }[] = [
    { label: productCodeLabel, value: productCode },
    { label: netWeightLabel, value: netWeight, alwaysShow: true },
    { label: upcLabel, value: upc },
    { label: volumeLabel, value: volume },
    { label: gtinLabel, value: gtin },
    { label: heightLabel, value: height },
    { label: unitSizeLabel, value: unitSize },
    { label: lengthLabel, value: length },
    { label: caseCountLabel, value: caseCount },
    { label: widthLabel, value: width },
    { label: caseSizeLabel, value: caseSize },
  ];

  const visibleFields = fields.filter(({ value, alwaysShow }) => alwaysShow || value);

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className={styles.productDetailsWrapper}>
      <h2 className={styles.productDetailsHeading}>{headline}</h2>
      <dl className={styles.productDetailsGrid}>
        {visibleFields.map(({ label, value, alwaysShow }) => (
          <div key={label} className={styles.productDetailsRow}>
            <dt className={styles.productDetailsLabel}>{label}:</dt>
            <dd className={styles.productDetailsValue}>
              {value ?? (alwaysShow ? notAvailable : null)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
